// Users tab — usage stats grid + access grid (with create/edit/status
// modal).
//
// Mirrors web/scripts/admin.js's Users tab logic: the usage grid
// (/admin/users) shows per-user query/session counts, the access grid
// (/admin/users/access) is the source of truth for authorization
// (role, status, org memberships) and is the surface for the Add User
// modal + Edit + Deactivate buttons.

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  AuthorizedUserRow,
  OrgScope,
  UserRow,
} from "../../api/admin";
import { adminApi } from "../../api/admin";
import {
  Chart,
  ErrorBlock,
  KpiCard,
  LoadingBlock,
  formatDateTime,
  formatNumber,
  horizontalBarOption,
} from "./_shared";
import { UserModal } from "./UserModal";

interface Props {
  bearer: string;
  scope: OrgScope;
  isSuperAdmin: boolean;
}

export function UsersTab({ bearer, scope, isSuperAdmin }: Props) {
  const [usage, setUsage] = useState<UserRow[]>([]);
  const [access, setAccess] = useState<AuthorizedUserRow[]>([]);
  const [orgs, setOrgs] = useState<string[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [overviewKpis, setOverviewKpis] = useState<{
    total_users?: number;
    active_7d?: number;
    avg_sessions_per_user_30d?: number;
    top_users_by_sessions?: Array<{ user_email: string; session_count: number }>;
  } | null>(null);
  const [overviewError, setOverviewError] = useState<Error | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [editTarget, setEditTarget] = useState<AuthorizedUserRow | null>(null);

  const refreshAccess = useCallback(() => {
    let cancelled = false;
    setError(null);
    adminApi
      .userAccess(bearer, { ...scope, include_inactive: showInactive ? "true" : undefined })
      .then((rows) => {
        if (!cancelled) setAccess(rows);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope, showInactive]);

  // Headline KPIs — sourced from /admin/overview's users_kpis block.
  useEffect(() => {
    let cancelled = false;
    adminApi
      .overview(bearer, scope)
      .then((d) => {
        if (!cancelled) setOverviewKpis(d.users_kpis ?? {});
      })
      .catch((e: Error) => {
        if (!cancelled) setOverviewError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  // Usage grid (/admin/users) + Access grid (/admin/users/access) load
  // together; "Show inactive" only re-runs the access fetch.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([adminApi.users(bearer, scope), adminApi.userAccess(bearer, scope)])
      .then(([usageRows, accessRows]) => {
        if (cancelled) return;
        setUsage(usageRows);
        setAccess(accessRows);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  // Refresh access whenever the inactive toggle changes (without
  // touching the usage grid).
  useEffect(() => refreshAccess(), [refreshAccess]);

  // Fetch the org list for the modal multi-select. Super Admin uses
  // all published orgs; Org Admin is locked to their own.
  useEffect(() => {
    let cancelled = false;
    if (isSuperAdmin) {
      adminApi
        .organizations(bearer, true)
        .then((o) => {
          if (!cancelled) setOrgs(o);
        })
        .catch(() => {});
    } else {
      setOrgs(scope.organization ? [scope.organization] : []);
    }
    return () => {
      cancelled = true;
    };
  }, [bearer, isSuperAdmin, scope.organization]);

  const handleSetStatus = useCallback(
    async (email: string, status: "active" | "inactive") => {
      const verb = status === "active" ? "Reactivate" : "Deactivate";
      // eslint-disable-next-line no-alert
      if (!window.confirm(`${verb} ${email}?`)) return;
      try {
        await adminApi.setUserStatus(bearer, email, status);
        refreshAccess();
      } catch (e) {
        setError(e as Error);
      }
    },
    [bearer, refreshAccess],
  );

  const usageColDefs = useMemo<ColDef<UserRow>[]>(
    () => [
      { field: "user_email", headerName: "Email", flex: 2 },
      { field: "organization", headerName: "Org", width: 160 },
      { field: "session_count", headerName: "Sessions", width: 110, type: "numericColumn" },
      { field: "total_queries", headerName: "Queries", width: 110, type: "numericColumn" },
      {
        field: "last_active",
        headerName: "Last active",
        width: 200,
        valueFormatter: (p) => formatDateTime(p.value as string),
      },
      { field: "feedback_given", headerName: "Feedback", width: 110, type: "numericColumn" },
    ],
    [],
  );

  const accessColDefs = useMemo<ColDef<AuthorizedUserRow>[]>(
    () => [
      { field: "email", headerName: "Email", flex: 2 },
      { field: "name", headerName: "Name", flex: 1 },
      {
        field: "role",
        headerName: "Role",
        width: 130,
        cellRenderer: (p: { value: string }) => <RoleBadge role={p.value} />,
      },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        cellRenderer: (p: { value: string }) =>
          p.value === "inactive" ? (
            <span className="inline-block rounded-full bg-neutral-200 text-neutral-700 text-[11px] font-medium px-2 py-0.5">
              Inactive
            </span>
          ) : (
            <span className="inline-block rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-medium px-2 py-0.5">
              Active
            </span>
          ),
      },
      {
        headerName: "Organizations",
        flex: 2,
        valueGetter: (p) => (p.data?.organizations || []).map((o) => o.name).join(", "),
      },
      {
        headerName: "Default Org",
        width: 160,
        valueGetter: (p) =>
          (p.data?.organizations || []).find((o) => o.is_default)?.name || "",
      },
      { field: "org_role", headerName: "Org Role", width: 160 },
      { field: "org_dept_name", headerName: "Department", width: 180 },
      {
        headerName: "",
        width: 230,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (p: { data: AuthorizedUserRow }) => {
          const isInactive = p.data.status === "inactive";
          return (
            <div className="flex items-center gap-2 h-full">
              <button
                type="button"
                className="rounded-md border border-line px-2 h-7 text-xs hover:bg-chip"
                onClick={() => {
                  setEditTarget(p.data);
                  setModalMode("edit");
                }}
              >
                Edit
              </button>
              {isInactive ? (
                <button
                  type="button"
                  className="rounded-md border border-emerald-300 text-emerald-700 px-2 h-7 text-xs hover:bg-emerald-50"
                  onClick={() => handleSetStatus(p.data.email, "active")}
                >
                  Reactivate
                </button>
              ) : (
                <button
                  type="button"
                  className="rounded-md border border-red-300 text-red-700 px-2 h-7 text-xs hover:bg-red-50"
                  onClick={() => handleSetStatus(p.data.email, "inactive")}
                >
                  Deactivate
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [handleSetStatus],
  );

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Users" value={formatNumber(overviewKpis?.total_users)} />
        <KpiCard label="Active (7d)" value={formatNumber(overviewKpis?.active_7d)} />
        <KpiCard
          label="Avg Sessions / User (30d)"
          value={
            overviewKpis?.avg_sessions_per_user_30d != null
              ? overviewKpis.avg_sessions_per_user_30d.toFixed(1)
              : "—"
          }
        />
        <KpiCard label="Top Users (30d)" value="">
          {overviewKpis?.top_users_by_sessions && overviewKpis.top_users_by_sessions.length > 0 ? (
            <Chart
              height={60}
              option={horizontalBarOption(
                overviewKpis.top_users_by_sessions.map((u) => ({
                  label: u.user_email.split("@")[0],
                  value: u.session_count,
                })),
              )}
            />
          ) : (
            <div className="text-xs text-neutral-400">—</div>
          )}
        </KpiCard>
      </div>
      {overviewError && <ErrorBlock error={overviewError} />}

      {error && <ErrorBlock error={error} />}

      <div className="ag-theme-quartz" style={{ width: "100%", height: 360 }}>
        <AgGridReact
          rowData={usage}
          columnDefs={usageColDefs}
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
          pagination={true}
          paginationPageSize={25}
        />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-900">User Access</h3>
        <div className="flex items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2 text-neutral-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-line text-brand focus:ring-brand"
            />
            <span className="text-xs">Show inactive</span>
          </label>
          <button
            type="button"
            onClick={() => {
              setEditTarget(null);
              setModalMode("create");
            }}
            className="rounded-md bg-brand text-white text-sm font-medium h-9 px-3 hover:bg-brand-600"
          >
            + Add User
          </button>
        </div>
      </div>

      <div className="ag-theme-quartz" style={{ width: "100%", height: 480 }}>
        <AgGridReact
          rowData={access}
          columnDefs={accessColDefs}
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
          pagination={true}
          paginationPageSize={25}
        />
      </div>

      {modalMode && (
        <UserModal
          bearer={bearer}
          mode={modalMode}
          target={modalMode === "edit" ? editTarget : null}
          orgs={orgs}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setModalMode(null)}
          onSaved={() => {
            setModalMode(null);
            refreshAccess();
          }}
        />
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: string | null | undefined }) {
  const v = role || "";
  const cls =
    v === "Super Admin"
      ? "bg-red-100 text-red-700"
      : v === "Admin"
        ? "bg-blue-100 text-blue-700"
        : v === "Demo User"
          ? "bg-amber-100 text-amber-700"
          : "bg-neutral-100 text-neutral-700";
  return (
    <span className={`inline-block rounded-full text-[11px] font-medium px-2 py-0.5 ${cls}`}>
      {v || "—"}
    </span>
  );
}
